import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ExternalPromo, PaymentMethod, Promotion } from '../models/cart.model';
import { Restaurant, Table, TippingConfig, WaiterOption } from '../models/config.model';
import { MenuActionObject } from '../models/menu.model';
import { Zone } from '../models/config.model';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  public hasFees$ = new BehaviorSubject<boolean>(false);
  public logoUrl$ = new BehaviorSubject<string>('');

  table = signal<Table>(null!);
  restaurant = signal<Restaurant>(null!);
  zone = signal<Zone>(null!);
  isGridLayout = signal<boolean>(false);

  menuActions = signal<MenuActionObject[]>(null!);
  ezetapMachineId = signal<string>(null!);
  paymentMethods = signal<PaymentMethod[]>(null!);
  promotions = signal<Promotion[]>(null!);
  externalPromos = signal<ExternalPromo[]>(null!);
  // True when the app booted straight onto a menu-list link (external promo links).
  // One-shot: MenuListComponent consumes it so later in-app visits aren't re-validated.
  private externalLinkEntry = false;
  filteredWaiterOptions = signal<WaiterOption[]>(null!);
  private waiterOptionBlockedUntil = new Map<string, number>();

  menuBackgroundUrl = signal<string | null>(null);
  voucherName = signal<string | null>(null);
  isPayNowEnabled: boolean = false;
  tippingConfig = signal<TippingConfig | null>(null);
  hasLandingPage: boolean = false;
  ctaText?: string | null = null;
  ctaUrl?: string | null = null;

  transactionLoaded = signal(true);

  public setTableId(tableId: string): void {
    localStorage.setItem('table_id', tableId);
  }

  public getTableId(): string | null {
    return this.table()?.id ?? this.getStoredTableId();
  }

  public getStoredTableId(): string | null {
    return localStorage.getItem('table_id');
  }

  public markExternalLinkEntry(): void {
    this.externalLinkEntry = true;
  }

  public consumeExternalLinkEntry(): boolean {
    const isEntry = this.externalLinkEntry;
    this.externalLinkEntry = false;
    return isEntry;
  }

  // data.service.ts
  public setUserId(): void {
    if (!localStorage.getItem('userId')) {
      localStorage.setItem('userId', uuidv4());
    }
  }

  public getUserId(): string {
    return localStorage.getItem('userId') || '';
  }

  // Waiter Options Management

  setWaiterOptionBlocked(optionId: string): void {
    const endTime = Date.now() + 15000; // 15 seconds
    this.waiterOptionBlockedUntil.set(optionId, endTime);
  }

  isWaiterOptionBlocked(optionId: string): boolean {
    const endTime = this.waiterOptionBlockedUntil.get(optionId);
    if (!endTime) return false;

    if (Date.now() >= endTime) {
      this.waiterOptionBlockedUntil.delete(optionId);
      return false;
    }

    return true;
  }

  getWaiterOptionRemainingTime(optionId: string): number {
    const endTime = this.waiterOptionBlockedUntil.get(optionId);
    if (!endTime) return 0;

    return Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
  }

}
